import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Send, MessageCircle, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getWsUrl() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return backendUrl.replace(/^http/, "ws");
}

export default function ChatPanel({ projectId, isOpen, onClose }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const reconnectRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  // Load existing messages
  useEffect(() => {
    if (!isOpen || !projectId) return;
    axios.get(`${API}/chat/${projectId}/messages`, { withCredentials: true })
      .then(res => { setMessages(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [isOpen, projectId]);

  // WebSocket connection
  useEffect(() => {
    if (!isOpen || !projectId || !user) return;

    const getToken = () => {
      const cookies = document.cookie.split(";").map(c => c.trim());
      const at = cookies.find(c => c.startsWith("access_token="));
      if (at) return at.split("=")[1];
      const st = cookies.find(c => c.startsWith("session_token="));
      if (st) return st.split("=")[1];
      return "";
    };

    const connect = () => {
      const token = getToken();
      const wsUrl = `${getWsUrl()}/ws/chat/${projectId}?token=${token}`;
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnected(true);
        if (reconnectRef.current) {
          clearTimeout(reconnectRef.current);
          reconnectRef.current = null;
        }
      };

      ws.onmessage = (event) => {
        const msg = JSON.parse(event.data);
        setMessages(prev => {
          if (prev.find(m => m.id === msg.id)) return prev;
          return [...prev, msg];
        });
      };

      ws.onclose = () => {
        setConnected(false);
        // Reconnect after 3 seconds
        reconnectRef.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => {
        setConnected(false);
      };

      wsRef.current = ws;
    };

    connect();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      if (reconnectRef.current) {
        clearTimeout(reconnectRef.current);
      }
    };
  }, [isOpen, projectId, user]);

  // Auto-scroll
  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text) return;
    // Try WebSocket first, fallback to REST
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text }));
    } else {
      try {
        const res = await axios.post(`${API}/chat/${projectId}/messages`, { text }, { withCredentials: true });
        setMessages(prev => {
          if (prev.find(m => m.id === res.data.id)) return prev;
          return [...prev, res.data];
        });
      } catch (err) {
        console.error("Failed to send message:", err);
        return;
      }
    }
    setInput("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!isOpen) return null;

  return (
      <div
        data-testid="chat-panel"
        className="glass-surface rounded-lg overflow-hidden flex flex-col"
        style={{ height: "480px" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-[#00F0FF]" />
            <span className="font-['Outfit'] text-sm font-medium text-white">Projekt-Chat</span>
            <span className={`w-2 h-2 rounded-full ${connected ? "bg-[#00F0FF] animate-glow-pulse" : "bg-[#FF3B30]"}`} />
          </div>
          <button data-testid="chat-close-btn" onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded bg-white/5 hover:bg-white/10 transition-colors">
            <X className="w-4 h-4 text-[#A0A0B0]" />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading && (
            <div className="flex justify-center py-8">
              <Loader2 className="w-5 h-5 text-[#00F0FF] animate-spin" />
            </div>
          )}

          {!loading && messages.length === 0 && (
            <div className="text-center py-12">
              <MessageCircle className="w-10 h-10 text-[#606070] mx-auto mb-3" />
              <p className="text-sm text-[#606070]">Noch keine Nachrichten. Starte die Konversation!</p>
            </div>
          )}

          {messages.map((msg) => {
            const isOwn = msg.sender_id === user?.user_id;
            const isSystem = msg.sender_id === "system" || msg.type === "system";

            if (isSystem) {
              return (
                <div key={msg.id} className="text-center">
                  <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] px-3 py-1 bg-white/[0.02] rounded-full">
                    {msg.text}
                  </span>
                </div>
              );
            }

            return (
              <div key={msg.id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] ${isOwn ? "order-2" : ""}`}>
                  {!isOwn && (
                    <span className="text-[10px] text-[#606070] font-['JetBrains_Mono'] ml-1 mb-0.5 block">{msg.sender_name}</span>
                  )}
                  <div
                    data-testid={`chat-message-${msg.id}`}
                    className={`px-3 py-2 rounded-lg text-sm ${
                      isOwn
                        ? "bg-[#00F0FF]/10 border border-[#00F0FF]/20 text-white"
                        : "bg-white/5 border border-white/10 text-[#A0A0B0]"
                    }`}
                  >
                    {msg.text}
                  </div>
                  <span className="text-[9px] text-[#606070] font-['JetBrains_Mono'] ml-1 mt-0.5 block">
                    {new Date(msg.timestamp).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="border-t border-white/5 p-3 flex items-center gap-2">
          <input
            data-testid="chat-input"
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Nachricht schreiben..."
            className="flex-1 bg-black/50 border border-white/10 text-white focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] px-3 py-2 placeholder:text-white/30 text-sm transition-colors outline-none rounded"
          />
          <button
            data-testid="chat-send-btn"
            onClick={handleSend}
            disabled={!input.trim()}
            className={`w-9 h-9 flex items-center justify-center rounded transition-all ${
              input.trim()
                ? "bg-[#00F0FF]/10 border border-[#00F0FF]/30 text-[#00F0FF] hover:bg-[#00F0FF]/20"
                : "bg-white/5 text-[#606070] cursor-not-allowed"
            }`}
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
  );
}
